import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import CENDropDownIcon from "@tCentro/Icons/CENDropDownIcon";
import { COLORS, NISCO, TYPOGRAPHY } from "@tCentro/theme/design";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    pagination: getProductListDataViewProperty(state, "pagination"),
    siteName: state.context.website.name,
});

const mapDispatchToProps = {
    addProductFilters,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListNISSortSelectStyles {
    select?: SelectPresentationProps;
    niscoSelect?: SelectPresentationProps;
}

export const NISSortSelectStyles: ProductListNISSortSelectStyles = {
    select: {
        iconProps: {
            src: CENDropDownIcon,
            css: css`
                > svg {
                    width: 12px !important;
                    height: 12px !important;
                    top: 12px !important;
                    right: 24px !important;
                }
            `,
        },
        labelProps: {
            css: css`
                padding: 0 !important;
                width: 150px !important;
                font-family: ${NISCO.TYPOGRAPHY.fontFamily.title};
                min-height: auto;
                @media (min-width: 992px) and (max-width: 1199px) {
                    width: 100px !important;
                }
            `,
        },
        cssOverrides: {
            formField: css`
                border: 1px solid ${COLORS.border.darkShade};
                border-radius: 16px;
                background: ${COLORS.white} !important;
                width: 100% !important;
                float: right !important;
                align-items: center;
                label {
                    font-size: ${TYPOGRAPHY.size.smaller} !important;
                    font-weight: ${TYPOGRAPHY.weight.base} !important;
                    line-height: 14px !important;
                    color: #747b83 !important;
                }

                @media (min-width: 992px) and (max-width: 1199px) {
                    width: 100% !important;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            width: 160px !important;
                            margin-right: 12px;
                        `,
                        css`
                            margin-right: 10px;
                            width: 160px !important;
                        `,
                        css`
                            width: auto !important;
                        `,
                        null,
                        null,
                    ])}
            `,
            formInputWrapper: css`
                width: 100% !important;
                select {
                    border: none !important;
                    border-radius: 16px !important;
                    font-size: ${TYPOGRAPHY.size.smaller} !important;
                    font-weight: ${TYPOGRAPHY.weight.bold} !important;
                    line-height: 14px !important;
                    color: #333333 !important;
                    padding-right: 24px !important;
                    font-family: ${NISCO.TYPOGRAPHY.fontFamily.title} !important;
                    padding-top: 2px !important;
                }
                span {
                    color: ${getProp("theme.typography.p.color")} !important;
                    right: 2px !important;
                }
                @media (min-width: 992px) and (max-width: 1199px) {
                    span {
                        right: 3px !important;
                    }
                }
            `,
        },
    },
};

const styles = NISSortSelectStyles;

const NISProductListSortSelect = ({ addProductFilters, pagination, siteName }: Props) => {
    if (!pagination) {
        return null;
    }

    const onChangeSortHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        addProductFilters({ sort: event.currentTarget.value });
    };

    return (
        <Select
            {...styles.select}
            label={translate("Sort by:")}
            value={pagination.sortType}
            labelPosition="left"
            preventEllipsis={true}
            onChange={onChangeSortHandler}
            data-test-selector="NISProductListSortSelect"
        >
            {pagination.sortOptions.map(sortOption => (
                <option key={sortOption.sortType} value={sortOption.sortType}>
                    {sortOption.displayName}
                </option>
            ))}
        </Select>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(NISProductListSortSelect),
    definition: {
        group: "Product List",
        displayName: "NIS Sort Select",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
